import { useColor } from '@/composables/color';
import type { CanvasEx } from '@/typings/canvas';
import type { ColorObject } from '@/typings/libraries';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ElementMapOption } from '@/components/DesignStudio/types';

/**
 * What a canvas element has to expose for its shadow color options to be managed here. Texts and
 *  image placeholders implement the same shape on purpose, so every panel shares this logic.
 */
export interface ShadowColorElement {
    shadowColor?: ElementMapOption;
    shadowColorsMap?: Map<number, ElementMapOption>;
    shadowColorOption?: number;
    shadowColorLibraryId?: number;
    setShadowColor(color: string): void;
    setEditedProp(...props: string[]): void;
}

/** The slice of the panel's local model this composable keeps in sync with the element. */
export interface ShadowColorModel {
    shadowColorOption: number;
    shadowColorLibraryId?: number;
    shadowColorsMap: Map<number, ElementMapOption>;
}

export interface ShadowColorOptions<T extends ShadowColorElement> {
    /** Getter, not the value: the selected element changes while the panel stays mounted. */
    element: () => T | undefined;
    canvas: () => CanvasEx;
    editMode: () => boolean | undefined;
    model: ShadowColorModel;
    /** Opens the library picker, which lives in the AngularJS controller of each step. */
    openLibrarySelector: () => void;
}

export interface ShadowColorOptionsApi {
    /** The css an option resolves to, without touching the element. */
    resolveColorCss: (option: number, value?: ElementMapOption) => string | undefined;
    loadColorIntoElement: (option: number, value?: ElementMapOption) => void;
    onColorSelected: (value: ElementMapOption, option?: number) => void;
    previewColorOption: (color?: ElementMapOption) => void;
    removeColorOption: (position: number) => void;
    onColorOptionsReordered: (reordered: KeyValueDto<number, ElementMapOption>[]) => void;
    pasteColors: (colors: Map<number, ElementMapOption>, libraryId?: number) => void;
    pickColorLibrary: () => void;
    removeColorLibrary: () => void;
}

/**
 * Color options for a drop shadow: the merchant defines one or more colors (or points at a color
 *  library) and the buyer picks one in the storefront.
 *
 * Shared by every shadow panel through useShadowSettings, which only differ in which element they
 *  are pointed at.
 */
export function useShadowColorOptions<T extends ShadowColorElement>(
    options: ShadowColorOptions<T>
): ShadowColorOptionsApi {
    const { hexToRgba } = useColor();
    const { model } = options;

    const setEditedProp = (...props: string[]) => {
        if (options.editMode()) {
            options.element()?.setEditedProp(...props);
        }
    };

    /**
     * The element's map, created on the spot when it hasn't got one yet: writing through an
     *  optional chain would drop the color without a word.
     */
    const colorsMapOf = (element: T) => {
        if (!element.shadowColorsMap) {
            element.shadowColorsMap = new Map<number, ElementMapOption>();
        }
        return element.shadowColorsMap;
    };

    /**
     * shadowColor is the persisted copy of option 1, so it has to follow the map every time the
     *  positions change. Texts get away without this because they re-derive it while saving (see
     *  processTextBeforeSaving), image placeholders are serialized exactly as they are.
     */
    const syncDefaultColor = (element: T, colorMap: Map<number, ElementMapOption>) => {
        element.shadowColor = colorMap.get(1);
    };

    const resolveColorCss = (option: number, value?: ElementMapOption) => {
        const color = ((value || options.element()?.shadowColorsMap?.get(option)) as ColorObject) || undefined;
        if (!color) return undefined;

        return color.alpha < 1 ? hexToRgba(color.hex, color.alpha) : color.hex;
    };

    /** Resolves a color option to css and paints the element with it. */
    const loadColorIntoElement = (option: number, value?: ElementMapOption) => {
        const element = options.element();
        if (!element) return;

        const rgba = resolveColorCss(option, value);
        if (!rgba) return;

        element.setShadowColor(rgba);
        element.shadowColorOption = model.shadowColorOption = option;

        options.canvas().requestRenderAll();
    };

    const onColorSelected = (value: ElementMapOption, option = 1) => {
        const element = options.element();
        if (!element || !value) return;

        setEditedProp('shadowColorsMap', 'shadowColor');

        const colorMap = colorsMapOf(element);
        colorMap.set(option, value);
        model.shadowColorsMap = new Map(colorMap);
        syncDefaultColor(element, colorMap);

        loadColorIntoElement(option);
    };

    /** Paints a color without storing it, so the merchant can try options out. */
    const previewColorOption = (color?: ElementMapOption) => {
        loadColorIntoElement(model.shadowColorOption || 1, color);
    };

    const removeColorOption = (position: number) => {
        const element = options.element();
        if (!element) return;

        setEditedProp('shadowColorsMap', 'shadowColor');
        const colorMap = colorsMapOf(element);
        colorMap.delete(position);
        model.shadowColorsMap.delete(position);

        // Positions keep their number when one is removed. The selector only ever removes the last
        //  option of the list and never the only one left, so there is always a first one to fall
        //  back to; reading it instead of hardcoding 1 keeps this right if that ever changes.
        const [firstOption] = colorMap.keys();
        if (firstOption !== undefined) {
            loadColorIntoElement(firstOption);
        }
        syncDefaultColor(element, colorMap);
        model.shadowColorsMap = new Map(model.shadowColorsMap);
    };

    /** Dragging an option changes which one is offered first, so positions are rebuilt from scratch. */
    const onColorOptionsReordered = (reordered: KeyValueDto<number, ElementMapOption>[]) => {
        const element = options.element();
        if (!element) return;

        const colorMap = new Map<number, ElementMapOption>();
        // KeyValueDto declares `value` optional, but the selector only emits populated options
        reordered.forEach(({ value }, index) => colorMap.set(index + 1, value as ElementMapOption));

        setEditedProp('shadowColorsMap', 'shadowColor');
        element.shadowColorsMap = colorMap;
        syncDefaultColor(element, colorMap);
        model.shadowColorsMap = new Map(colorMap);
        loadColorIntoElement(1);
    };

    /** Overwrites the whole option set with a clipboard clip pasted from another element's color panel. */
    const pasteColors = (colors: Map<number, ElementMapOption>, libraryId?: number) => {
        const element = options.element();
        if (!element) return;

        setEditedProp('shadowColorsMap', 'shadowColor', 'shadowColorLibraryId');
        const colorMap = new Map(colors);
        element.shadowColorsMap = colorMap;
        element.shadowColorLibraryId = libraryId;
        syncDefaultColor(element, colorMap);
        model.shadowColorsMap = new Map(colorMap);
        model.shadowColorLibraryId = libraryId;
        // Set explicitly instead of relying on loadColorIntoElement's fallback: a clip copied from a
        //  purely library-linked shadow (no manual colors) has nothing at position 1 to fall back to.
        element.shadowColorOption = model.shadowColorOption = 1;
        loadColorIntoElement(1);
    };

    const pickColorLibrary = () => {
        const element = options.element();
        if (!element) return;

        element.shadowColorOption = model.shadowColorOption = 1;
        options.openLibrarySelector();
    };

    const removeColorLibrary = () => {
        const element = options.element();
        if (!element) return;

        element.shadowColorLibraryId = undefined;
        setEditedProp('shadowColorLibraryId');
        loadColorIntoElement(1);

        model.shadowColorLibraryId = undefined;
    };

    return {
        resolveColorCss,
        loadColorIntoElement,
        onColorSelected,
        previewColorOption,
        removeColorOption,
        onColorOptionsReordered,
        pasteColors,
        pickColorLibrary,
        removeColorLibrary
    };
}
